import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Search, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { API, studentLabel } from "@/lib/attendance";

export default function GuestPicker({ token, excludeIds, onClose, onAdd, onAuthError }) {
  const [tab, setTab] = useState("search");
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-end sm:items-center justify-center" data-testid="guest-picker">
      <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-4 pt-4">
          <div className="text-lg font-bold text-slate-900">Aggiungi ospite</div>
          <Button variant="ghost" onClick={onClose} className="h-11 w-11 p-0" data-testid="guest-picker-close-btn" aria-label="Chiudi">
            <X className="w-5 h-5" />
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-2 px-4 py-3">
          <TabBtn active={tab === "search"} onClick={() => setTab("search")} testid="guest-tab-search">Cerca iscritto</TabBtn>
          <TabBtn active={tab === "new"} onClick={() => setTab("new")} testid="guest-tab-new">Nuovo nome</TabBtn>
        </div>
        {tab === "search"
          ? <SearchTab token={token} excludeIds={excludeIds} onAdd={onAdd} onAuthError={onAuthError} />
          : <NewTab onAdd={onAdd} />}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={cn("h-11 rounded-xl text-sm font-semibold border-2", active ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600")}>
      {children}
    </button>
  );
}

function SearchTab({ token, excludeIds, onAdd, onAuthError }) {
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    axios.get(`${API}/teacher/students`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setAll(r.data)).catch(onAuthError);
    // eslint-disable-next-line
  }, [token]);
  const results = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (t.length < 2) return [];
    return all.filter((s) => !excludeIds.includes(s.id) && `${s.cognome} ${s.nome} ${s.nome} ${s.cognome}`.toLowerCase().includes(t)).slice(0, 30);
  }, [all, q, excludeIds]);

  return (
    <div className="px-4 pb-4 flex flex-col min-h-0">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Scrivi il cognome..." className="pl-9 h-12 text-base" data-testid="guest-search-input" />
      </div>
      <div className="mt-3 overflow-y-auto space-y-2 max-h-[50vh]">
        {results.map((s) => (
          <button key={s.id} onClick={() => onAdd({ registration_id: s.id, nome: s.nome, cognome: s.cognome, anno: s.anno })}
            className="w-full min-h-[52px] rounded-xl border-2 border-slate-200 px-4 text-left font-semibold text-slate-800 flex items-center justify-between"
            data-testid={`guest-result-${s.id}`}>
            {studentLabel(s)} <Plus className="w-4 h-4 text-[#C85A32]" />
          </button>
        ))}
        {q.trim().length >= 2 && !results.length && (
          <div className="text-sm text-slate-500 py-3 text-center" data-testid="guest-no-results">Nessun iscritto trovato. Usa "Nuovo nome".</div>
        )}
      </div>
    </div>
  );
}

function NewTab({ onAdd }) {
  const [nome, setNome] = useState("");
  const [cognome, setCognome] = useState("");
  const [anno, setAnno] = useState("");
  const valid = nome.trim() && cognome.trim() && /^\d{4}$/.test(anno);
  return (
    <div className="px-4 pb-4 space-y-3">
      <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome" className="h-12 text-base" data-testid="guest-new-nome" />
      <Input value={cognome} onChange={(e) => setCognome(e.target.value)} placeholder="Cognome" className="h-12 text-base" data-testid="guest-new-cognome" />
      <Input value={anno} onChange={(e) => setAnno(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" placeholder="Anno di nascita (es. 2014)" className="h-12 text-base" data-testid="guest-new-anno" />
      <p className="text-xs text-slate-500">Resta solo nel registro di questa lezione, non viene aggiunto agli iscritti.</p>
      <Button disabled={!valid} onClick={() => onAdd({ registration_id: null, nome: nome.trim(), cognome: cognome.trim(), anno })}
        className="w-full h-12 text-base bg-[#C85A32] hover:bg-[#a94a28]" data-testid="guest-new-add-btn">
        Aggiungi alla lezione
      </Button>
    </div>
  );
}
